interface TokenData {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

class TokenManager {
  private static instance: TokenManager;
  private tokens: TokenData | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private tokenSetTime: number | null = null;

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  setTokens(tokens: TokenData): void {
    this.tokens = tokens;
    this.tokenSetTime = Date.now();
    localStorage.setItem('tokens', JSON.stringify(tokens));
    localStorage.setItem('tokenSetTime', this.tokenSetTime.toString());
    this.scheduleTokenRefresh(tokens.expires_in);
  }

  getTokens(): TokenData | null {
    if (this.tokens) {
      return this.tokens;
    }
    
    const stored = localStorage.getItem('tokens');
    const storedTime = localStorage.getItem('tokenSetTime');
    if (stored && storedTime) {
      this.tokens = JSON.parse(stored);
      this.tokenSetTime = parseInt(storedTime);
      
      // Check if tokens are still valid and schedule refresh if needed
      const timeElapsed = (Date.now() - this.tokenSetTime) / 1000;
      const timeRemaining = this.tokens!.expires_in - timeElapsed;
      
      if (timeRemaining > 0) {
        this.scheduleTokenRefresh(timeRemaining);
        return this.tokens;
      } else {
        // Tokens expired, try to refresh immediately
        this.refreshTokens();
        return this.tokens;
      }
    }
    
    return null;
  }

  getIdToken(): string | null {
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      return 'mock-id-token'; // Mock token for local dev
    }
    const tokens = this.getTokens();
    return tokens?.id_token || null;
  }

  getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.access_token || null;
  }

  clearTokens(): void {
    this.tokens = null;
    this.tokenSetTime = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    localStorage.removeItem('tokens');
    localStorage.removeItem('tokenSetTime');
  }

  isAuthenticated(): boolean {
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      return true; // Always authenticated in local dev
    }
    return this.getIdToken() !== null;
  }

  async logout(): Promise<void> {
    try {
      const apiUrl = this.getApiUrl();
      await fetch(`${apiUrl}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getIdToken()}`
        }
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      this.clearTokens();
      
      // Also log out from Cognito to enable user switching
      const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID;
      const cognitoUrl = process.env.REACT_APP_OAUTH_ISSUER;
      const redirectUri = encodeURIComponent(`${window.location.origin}/`);
      
      if (clientId && cognitoUrl) {
        const cognitoLogoutUrl = `${cognitoUrl}/logout?client_id=${clientId}&logout_uri=${redirectUri}`;
        window.location.href = cognitoLogoutUrl;
      }
    }
  }

  private scheduleTokenRefresh(expiresInSeconds: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    
    // Refresh 5 minutes before expiry (or immediately if less than 5 minutes remaining)
    const refreshTime = Math.max(0, (expiresInSeconds - 300) * 1000);
    
    this.refreshTimer = setTimeout(() => {
      this.refreshTokens();
    }, refreshTime);
  }

  private async refreshTokens(): Promise<void> {
    try {
      const currentTokens = this.tokens;
      if (!currentTokens?.refresh_token) {
        console.warn('No refresh token available');
        return;
      }

      const apiUrl = this.getApiUrl();
      const response = await fetch(`${apiUrl}/oauth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ refresh_token: currentTokens.refresh_token }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      if (data.success && data.tokens) {
        this.setTokens(data.tokens);
        console.log('Tokens refreshed successfully');
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear tokens and redirect to login on refresh failure
      this.clearTokens();
      window.location.href = '/';
    }
  }

  private getApiUrl(): string {
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }
    
    const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
    const applicationName = process.env.REACT_APP_APPLICATION_NAME || 'servicecatalog';
    const domain = process.env.REACT_APP_DOMAIN || 'locomotive10.com';
    
    return `https://api.${environment}.${applicationName}.${domain}`;
  }
}

export default TokenManager;
export type { TokenData };